import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import debounce from "lodash.debounce";
import kebabCase from "lodash.kebabcase";

import cc from "classcat";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Error,
  Row,
  Tag,
  TextInput,
  type MultilineTextInputProps,
} from "@narmi/design_system";
import { API_VERSION } from "byzantine/src/ApiHttp";
import Transaction from "byzantine/src/Transaction";
import {
  ContextForm,
  Dialog,
  useFormData,
  useNotificationContext,
  useTimeout,
} from "cerulean";
import { entities } from "byzantine";
import useEffectSkipFirstRender from "../../hooks/useEffectSkipFirstRender";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";

import styles from "./TransactionDetailsDialog.module.scss";
import CheckManager from "../CheckManager/CheckManager";

const NOTE_DEBOUNCE_TIME = 500; // in milliseconds
const CLEAR_SUCCESSFUL_NOTE_SAVE_TIME = 1000 * 5; // in milliseconds
const TAG_DEBOUNCE_TIME = 100; // in milliseconds
const TAG_CHAR_LIMIT = 16;

interface InvertedProps {
  onClick(): void;
}

const InvertedColorPlusCircle = memo(({ onClick }: InvertedProps) => (
  /* makes an icon with a grey circle and a white plus sign in it */
  <div
    onClick={onClick}
    onKeyUp={({ key }) => {
      if (key === "Enter") {
        onClick();
      }
    }}
    role="button"
  >
    <div className="inverted-color-plus-circle-container clickable">
      <div className="narmi-icon-solid-circle" />
      <div className="plus-sign-wrapper">
        <div className="narmi-icon-plus" />
      </div>
    </div>
  </div>
));

const InvertedColorMinusCircle = memo(({ onClick }: InvertedProps) => (
  /* makes an icon with a grey circle and a white minus sign in it */
  <div
    onClick={onClick}
    onKeyUp={({ key }) => {
      if (key === "Enter") {
        onClick();
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="inverted-color-minus-circle-container clickable">
      <div className="narmi-icon-solid-circle" />
      <div className="minus-sign-wrapper">
        <div className="narmi-icon-minus" />
      </div>
    </div>
  </div>
));

interface TagInputProps {
  addTag: (tagName: string) => void;
  currentTags: string[];
  isVisible: boolean;
}

const TagInput = memo(({ addTag, currentTags, isVisible }: TagInputProps) => {
  if (!isVisible) {
    return null;
  }

  const { l10n } = useLocalization();
  const { formData, onChange } = useFormData({ currentTags });

  useEffect(() => {
    onChange({ currentTags });
  }, [currentTags]);

  const validateNewTag = useCallback((newTag: string, data: any) => {
    // validate the to-be-added tag
    if (!newTag) {
      return l10n.getString("tag-validation-empty", null, "Cannot add empty tag.");
    }
    if (data.currentTags.includes(newTag)) {
      return l10n.getString("tag-validation-duplicate", null, "Cannot add duplicate tag.");
    }
    if (newTag.length > TAG_CHAR_LIMIT) {
      return l10n.getString("tag-validation-length", { charLimit: TAG_CHAR_LIMIT }, `Length cannot exceed ${TAG_CHAR_LIMIT} chars.`);
    }
    return null;
  }, [l10n]);

  const onSubmit = () => {
    addTag(formData.newTag);
    onChange({ newTag: "" });
  };

  return (
    <ContextForm data={formData} onChange={onChange} nativeForm={false}>
      <Row>
        <Row.Item>
          <ContextForm.Field
            validate={validateNewTag}
            style={{
              marginBottom: "var(--space-xs)",
              marginTop: "var(--space-xs)",
            }}
          >
            <TextInput placeholder={l10n.getString("tag-input-placeholder", null, "Tag")} field="newTag" />
          </ContextForm.Field>
        </Row.Item>
        <Row.Item shrink>
          <ContextForm.Action onSubmit={onSubmit} dangerouslyDisableShowLoading>
            <div style={{ marginTop: "var(--space-s)" }}>
              <Button testId="add-tag-button" kind="primary" type="button" label={l10n.getString("button-add-tag", null, "Add a new tag")} />
            </div>
          </ContextForm.Action>
        </Row.Item>
      </Row>
    </ContextForm>
  );
});

interface TagSectionProps {
  value?: string[];
  field: string;
  onChange?: (newTags: string[]) => void;
  tagErrors?: string;
}

const TransactionTagsSection = memo(
  ({ value = [], onChange, tagErrors }: TagSectionProps) => {
    /* displays the transaction tags for the specific transaction
     and allow users to add or delete tags */
    const { l10n } = useLocalization();
    const [isAddTagOpen, setIsAddTagOpen] = useState(false);
    const removeTag = useCallback(
      (tagName: string) => {
        onChange?.(value.filter((t) => t !== tagName));
      },
      [value, onChange]
    );

    const addTag = useCallback(
      (tagName: string) => {
        onChange?.([...value, tagName]);
      },
      [value, onChange]
    );

    const tagEditIcon = isAddTagOpen ? (
      <InvertedColorMinusCircle onClick={() => setIsAddTagOpen(false)} />
    ) : (
      <InvertedColorPlusCircle onClick={() => setIsAddTagOpen(true)} />
    );

    return (
      <>
        <Row alignItems="center" gapSize="xxs">
          <Row.Item shrink>
            <div className={styles.detailLabel}>{l10n.getString("transaction-detail-tags-label", null, "Tags")}</div>
          </Row.Item>
          <Row.Item shrink>{tagEditIcon}</Row.Item>
        </Row>
        <TagInput
          addTag={addTag}
          currentTags={value}
          isVisible={isAddTagOpen}
        />
        {value?.length > 0 && (
          <div className={styles.tagRowWrapper}>
            {value.map((tag) => (
              <Tag
                key={tag}
                kind="dismissible"
                label={tag}
                onDismiss={() => removeTag(tag)}
              />
            ))}
          </div>
        )}
        <Error error={tagErrors} />
      </>
    );
  }
);

interface TransactionAttributeProps {
  label: string;
  content?: ReactNode | null;
}

const TransactionAttribute = memo(
  ({ label, content }: TransactionAttributeProps) => {
    const { l10n } = useLocalization();
    const translatedLabel = l10n.getString(
      `transaction-detail-${kebabCase(label)}`,
      null,
      label
    );
    if (translatedLabel === "") {
      return null;
    }
    if (content === null || content === undefined || content === "") {
      return null;
    }

    return (
      <div className={styles.transactionRowWrapper}>
        <Row alignItems="center">
          <Row.Item shrink>
            <span className={styles.detailLabel}>{translatedLabel}</span>
          </Row.Item>
          <Row.Item>
            <div className={styles.detailContent}>{content}</div>
          </Row.Item>
        </Row>
      </div>
    );
  }
);

interface TransactionNoteLabelProps {
  noteStatus: string;
}

const TransactionNoteLabel = memo(
  ({ noteStatus }: TransactionNoteLabelProps) => {
    const { l10n } = useLocalization();

    return (
      <Row alignItems="center">
        <Row.Item>
          <div className={cc([styles.detailLabel, "margin--bottom--xs"])}>
            {l10n.getString("transaction-detail-notes-label", null, "Notes")}
          </div>
        </Row.Item>
        <Row.Item shrink>
          <div className="fontSize--s fontColor--secondary">{noteStatus}</div>
        </Row.Item>
      </Row>
    );
  }
);

interface TransactionNoteInputProps extends Omit<MultilineTextInputProps, "multiline"> {
  noteErrors?: string;
}

const TransactionNoteInput = memo(
  ({ noteErrors, ...otherProps }: TransactionNoteInputProps) => (
    /* Purpose of this wrapper is so we can pass an error that is not managed by ContextForm */
    <TextInput multiline={true} error={noteErrors} {...otherProps} />
  )
);

type TransactionDetailFooterProps = {
  transaction: Transaction;
  closeDialog: () => void;
  ReportIssueCallback: () => void;
};

const TransactionDetailFooter = memo(
  ({
    transaction,
    closeDialog,
    ReportIssueCallback,
  }: TransactionDetailFooterProps) => {
    const { l10n } = useLocalization();
    const { sendNotificationToParent } = useNotificationContext();
    const userFeatures = useUserFeatures();

    return (
      <>
        <div className={styles.footerSeparator} />
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            {userFeatures.show_transaction_contact_support && (
              <Button
                key="Report an issue"
                as="a"
                target="_blank"
                kind="negative"
                label={l10n.getString("button-report-issue", null, "Report an issue")}
                onClick={() => {
                  closeDialog();
                  ReportIssueCallback();
                }}
              />
            )}
          </Row.Item>
          <Row.Item shrink>
            <Button
              as="a"
              key="Download"
              kind="primary"
              label={l10n.getString("button-download", null, "Download")}
              href={`${API_VERSION}transactions/${transaction.id}?file_format=pdf`}
              onClick={() => {
                closeDialog();
                sendNotificationToParent({
                  type: "success",
                  text: l10n.getString("transaction-download-started", null, "Download started."),
                });
              }}
            />
          </Row.Item>
        </Row>
      </>
    );
  }
);

const updateTransactionNote = (
  transaction: Transaction,
  note: string,
  setNoteStatus: (status: string) => void,
  setNoteErrors: (error: string) => void,
  l10n: any
) =>
  /* returns a debounced function pointer that updates the transaction's note field */
  debounce(() => {
    setNoteErrors("");
    setNoteStatus(l10n.getString("transaction-note-saving", null, "Saving"));
    transaction
      .update({ note })
      .then(() => {
        setNoteStatus(l10n.getString("transaction-note-saved", null, "Saved"));
      })
      .catch((error) => {
        setNoteErrors(error);
        setNoteStatus("");
      });
  }, NOTE_DEBOUNCE_TIME);

const updateTransactionTags = (
  transaction: Transaction,
  tags: string[],
  setTagErrors: (error: string) => void
) =>
  /* returns a debounced function pointer that updates the transaction's note field */
  debounce(() => {
    setTagErrors("");
    transaction.update({ tags }).catch((error) => {
      setTagErrors(error);
    });
  }, TAG_DEBOUNCE_TIME);

interface TransactionDetailsDialogProps {
  isDialogOpen: boolean;
  displayedAmount: string;
  displayedResultingBalance?: string;
  isSourceMultipleAccounts: boolean;
  closeDialog: () => void;
  transaction: Transaction;
  transactionTitle: ReactNode;
  onDescriptionUpdate?: (description: string) => void;
  ReportIssueCallback: () => void;
}

const TransactionDetailsDialog = memo(
  ({
    isDialogOpen,
    displayedAmount,
    displayedResultingBalance,
    isSourceMultipleAccounts,
    closeDialog,
    transaction,
    transactionTitle,
    onDescriptionUpdate,
    ReportIssueCallback,
  }: TransactionDetailsDialogProps) => {
    /* dialog that displays the details of non-pending transactions */
    const { l10n } = useLocalization();
    const {
      account,
      amount,
      category,
      creation_date: creationDate,
      note,
      posted_date: postedDate,
      source,
      tags,
      overview: {
        principal,
        interest,
        escrow,
        fees,
        unapplied,
        raw_description: rawDescription,
      },
    } = transaction;
    const [tagErrors, setTagErrors] = useState("");
    const [noteStatus, setNoteStatus] = useState("");
    const [noteErrors, setNoteErrors] = useState("");
    const { formData, onChange } = useFormData({ note, tags });
    const { data: checkImages = [], send: fetchTransactionImages } =
      entities.transactions.useGetTransactionImages();

    // if already in the account page, render the account display name as plain text
    const accountDisplayNameNode = isSourceMultipleAccounts ? (
      <Button
        as="a"
        kind="plain"
        label={account?.nickname || account?.name}
        href={`/accounts/${account?.id}`}
      />
    ) : (
      account?.nickname || account?.name
    );

    /* retrieving check image if there are images to retrieve, and if we have not already retrieved them */
    useEffect(() => {
      if (isDialogOpen && !checkImages.length && postedDate) {
        fetchTransactionImages(transaction.id);
      }
    }, [isDialogOpen, fetchTransactionImages, transaction.id, postedDate]);

    /* auto-save changes to the transaction tags in a debounced manner */
    useEffectSkipFirstRender(() => {
      const debouncer = updateTransactionTags(
        transaction,
        formData.tags,
        setTagErrors
      );
      debouncer();
      return debouncer.cancel;
    }, [JSON.stringify(formData?.tags)]);

    /* auto-save changes to the transaction note in a debounced manner */
    useEffectSkipFirstRender(() => {
      const debouncer = updateTransactionNote(
        transaction,
        formData.note,
        setNoteStatus,
        setNoteErrors,
        l10n
      );
      debouncer();
      return debouncer.cancel;
    }, [formData?.note, l10n]);

    /* dismiss the successful "saved" message after a pre-set amount of time */
    useTimeout(
      () => {
        setNoteStatus((prev) => {
          const savedText = l10n.getString("transaction-note-saved", null, "Saved");
          if (prev === savedText) return "";
          return prev;
        });
      },
      CLEAR_SUCCESSFUL_NOTE_SAVE_TIME,
      [noteStatus, l10n]
    );

    /* clear the note status and errors when the dialog is closed */
    useEffect(() => {
      if (!isDialogOpen) {
        setNoteStatus("");
        setNoteErrors("");
        setTagErrors("");
      }
    }, [noteStatus, isDialogOpen]);

    return (
      isDialogOpen && (
        <Dialog
          isOpen={isDialogOpen}
          headerStyle="plain"
          footer={
            transaction.posted_date && (
              <TransactionDetailFooter
                transaction={transaction}
                closeDialog={closeDialog}
                ReportIssueCallback={ReportIssueCallback}
              />
            )
          }
          onUserDismiss={closeDialog}
          title={transactionTitle}
        >
          <div className={styles.transactionDialog}>
            <div className={styles.footerSeparator} />
            <div
              className={cc([
                styles.amount,
                {
                  [styles.negative]: amount < 0,
                  [styles.positive]: amount >= 0,
                },
              ])}
            >
              {displayedAmount}
            </div>
            <div className={styles.transactionAttributeContainer}>
              <TransactionAttribute label="Principal" content={principal} />
              <TransactionAttribute label="Interest" content={interest} />
              <TransactionAttribute label="Escrow" content={escrow} />
              <TransactionAttribute label="Fees" content={fees} />
              <TransactionAttribute label="Unapplied" content={unapplied} />
              <TransactionAttribute
                label="Transaction date"
                content={creationDate}
              />
              <TransactionAttribute label="Posted date" content={postedDate} />
              <TransactionAttribute
                label="Resulting balance"
                content={displayedResultingBalance}
              />
              {typeof account !== "undefined" && (
                <TransactionAttribute
                  label="Account"
                  content={accountDisplayNameNode}
                />
              )}
              <TransactionAttribute label="Transaction type" content={source} />
              <TransactionAttribute label="Category" content={category} />
              <TransactionAttribute
                label="Description"
                content={rawDescription}
              />
            </div>
            {postedDate && (
              <>
                <CheckManager images={checkImages} transactionId={transaction.id} onSaveSuccess={() => fetchTransactionImages(transaction.id)} onDescriptionUpdate={onDescriptionUpdate} />
                <div className={styles.separator} />
                <ContextForm
                  data={formData}
                  onChange={onChange}
                  nativeForm={false}
                >
                  <ContextForm.Field>
                    <TransactionTagsSection
                      field="tags"
                      tagErrors={tagErrors}
                    />
                  </ContextForm.Field>
                  <TransactionNoteLabel noteStatus={noteStatus} />
                  <ContextForm.Field className={styles.noteInputField}>
                    <TransactionNoteInput
                      field="note"
                      noteErrors={noteErrors}
                      placeholder={l10n.getString("note-input-placeholder", null, "Add a note...")}
                    />
                  </ContextForm.Field>
                </ContextForm>
              </>
            )}
          </div>
        </Dialog>
      )
    );
  }
);

export default TransactionDetailsDialog;
